import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { Trophy, Target, Calculator, TrendingUp } from 'lucide-react';

export const RecordsAndPredictions: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [baseDistance, setBaseDistance] = useState('5');
  const [baseTime, setBaseTime] = useState('');

  const { data: personalRecords, isLoading: recordsLoading } = useQuery({
    queryKey: ['personal-records'],
    queryFn: async () => {
      if (!user) return null;

      const { data: records, error } = await supabase
        .from('running_personal_records')
        .select('*')
        .order('distance_meters', { ascending: true });

      if (error) throw error;

      // Get recent runs to check for potential new PRs
      const { data: recentRuns, error: runsError } = await supabase
        .from('running_performance')
        .select('*')
        .order('recorded_date', { ascending: false })
        .limit(20);

      if (runsError) throw runsError;

      // Check for potential PRs
      const potentialPRs: any[] = [];
      const commonDistances = [1000, 5000, 10000, 21097, 42195]; // 1k, 5k, 10k, half, full marathon

      commonDistances.forEach(distance => {
        const existingRecord = records?.find(r => r.distance_meters === distance);
        const runsAtDistance = recentRuns?.filter(r => 
          Math.abs((r.distance_meters || 0) - distance) <= distance * 0.02 // 2% tolerance
        );

        if (runsAtDistance && runsAtDistance.length > 0) {
          const bestRecentRun = runsAtDistance.reduce((best, current) => 
            (current.time_seconds || Infinity) < (best.time_seconds || Infinity) ? current : best
          );

          if (!existingRecord || (bestRecentRun.time_seconds || Infinity) < existingRecord.time_seconds) {
            potentialPRs.push({
              distance,
              distanceKm: distance / 1000,
              currentTime: bestRecentRun.time_seconds,
              currentPace: bestRecentRun.pace_per_km_seconds,
              improvementSeconds: existingRecord ? existingRecord.time_seconds - (bestRecentRun.time_seconds || 0) : null,
              runDate: bestRecentRun.recorded_date,
              runId: bestRecentRun.id
            });
          }
        }
      });

      return { records: records || [], potentialPRs };
    },
    enabled: !!user,
  });

  const savePRMutation = useMutation({
    mutationFn: async (pr: any) => {
      const { error } = await supabase
        .from('running_personal_records')
        .insert({
          user_id: user!.id,
          distance_meters: pr.distance,
          time_seconds: pr.currentTime,
          pace_per_km_seconds: pr.currentPace,
          achieved_date: pr.runDate,
          performance_id: pr.runId,
          improvement_seconds: pr.improvementSeconds
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Personal record saved!' });
      queryClient.invalidateQueries({ queryKey: ['personal-records'] });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to save PR', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const calculatePredictions = () => {
    if (!baseTime || !baseDistance) return null;

    const [minutes, seconds] = baseTime.split(':').map(Number);
    const totalSeconds = minutes * 60 + (seconds || 0);
    const distanceKm = parseFloat(baseDistance);

    // Riegel's formula for race time prediction
    const predictions = [
      { distance: '1K', meters: 1000 },
      { distance: '5K', meters: 5000 },
      { distance: '10K', meters: 10000 },
      { distance: 'Half Marathon', meters: 21097 },
      { distance: 'Marathon', meters: 42195 }
    ].map(race => {
      const predictedSeconds = totalSeconds * Math.pow(race.meters / (distanceKm * 1000), 1.06);
      const mins = Math.floor(predictedSeconds / 60);
      const secs = Math.round(predictedSeconds % 60);
      const paceSeconds = predictedSeconds / (race.meters / 1000);
      const paceMins = Math.floor(paceSeconds / 60);
      const paceSecs = Math.round(paceSeconds % 60);
      
      return {
        ...race,
        time: `${mins}:${secs.toString().padStart(2, '0')}`,
        pace: `${paceMins}:${paceSecs.toString().padStart(2, '0')}`
      };
    });

    return predictions;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDistance = (meters: number) => {
    if (meters >= 1000) {
      const km = meters / 1000;
      return km % 1 === 0 ? `${km}K` : `${km.toFixed(1)}K`;
    }
    return `${meters}m`;
  };

  if (recordsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Personal Records & Race Predictions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96 flex items-center justify-center">Loading records...</div>
        </CardContent>
      </Card>
    );
  }

  const predictions = calculatePredictions();

  return (
    <div className="space-y-6">
      {/* Current Personal Records */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-600" />
            Current Personal Records
          </CardTitle>
          <CardDescription>Your best times by distance</CardDescription>
        </CardHeader>
        <CardContent>
          {personalRecords?.records.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No personal records yet. Complete some runs to set your first PRs!
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {personalRecords?.records.map(record => (
                <Card key={record.id} className="border-2 border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-lg font-semibold text-yellow-800">
                      {formatDistance(record.distance_meters)}
                    </div>
                    <div className="text-2xl font-bold">
                      {formatTime(record.time_seconds)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatTime(record.pace_per_km_seconds)}/km
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(record.achieved_date).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Potential New PRs */}
      {personalRecords?.potentialPRs && personalRecords.potentialPRs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Potential New Personal Records
            </CardTitle>
            <CardDescription>Recent runs that may be new PRs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {personalRecords.potentialPRs.map((pr, index) => (
              <Alert key={index} className="border-green-200 bg-green-50">
                <Trophy className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex items-center justify-between">
                    <div>
                      <strong>{formatDistance(pr.distance)} PR!</strong>
                      <div className="text-sm">
                        Time: {formatTime(pr.currentTime)} 
                        {pr.improvementSeconds && (
                          <Badge variant="secondary" className="ml-2">
                            -{pr.improvementSeconds}s improvement
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        From run on {new Date(pr.runDate).toLocaleDateString()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => savePRMutation.mutate(pr)}
                      disabled={savePRMutation.isPending}
                    >
                      Save PR
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Race Time Predictor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            Race Time Predictor
          </CardTitle>
          <CardDescription>Predict your race times based on a recent performance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="base-distance">Base Distance (km)</Label>
              <Input
                id="base-distance"
                type="number"
                step="0.1"
                value={baseDistance}
                onChange={(e) => setBaseDistance(e.target.value)}
                placeholder="5"
              />
            </div>
            <div>
              <Label htmlFor="base-time">Base Time (mm:ss)</Label>
              <Input
                id="base-time"
                value={baseTime}
                onChange={(e) => setBaseTime(e.target.value)}
                placeholder="25:30"
              />
            </div>
          </div>

          {predictions && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {predictions.map((pred, index) => (
                <Card key={index} className="border-2 border-blue-200 bg-blue-50">
                  <CardContent className="p-4 text-center">
                    <div className="text-lg font-semibold text-blue-800">
                      {pred.distance}
                    </div>
                    <div className="text-2xl font-bold">
                      {pred.time}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {pred.pace}/km pace
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};